import type { Booking, UpdateBookingDto } from '@/entities/bookings/booking.entity';
import { InvoicingType } from '@/entities/bookings/invoicing-type.enum';
import {
  CancellationFee,
  CounterpartyType,
  PriceRecalculationMode,
  type VehicleExtraType,
} from '@carvia/ros-client-types';
import {
  hasBookingPriceChanged,
  isAgencyPriceSplitBooking,
  isAtLeastHandedOver,
  isCustomerPaidBooking,
  requiresPriceDifferenceCounterparty,
} from '@/utils/price-difference-counterparty';
import {
  asShortTermPricing,
  getContractTotal,
  isLongTermPricing,
  isSameRentalLength,
} from '@/utils/pricing.utils';
import { inject, type InjectionKey, type Ref } from 'vue';

export type ChangedBookedExtraTypes = Partial<
  Record<VehicleExtraType, { vehicleExtraId: string; vehicleExtraOptionId: string }>
>;

export type ConfirmAndSaveArgs = {
  booking: Booking;
  updateBookingDto?: UpdateBookingDto;
  allowKeepOldPrice?: boolean;
  confirmOnlyWhenCancellationFee?: boolean;
  priceDifferenceCounterparty?: CounterpartyType;
  handoverAdjustment?: boolean;
};

export type ConfirmAndSaveResult = {
  status: 'saved' | 'cancelled' | 'failed';
  savedBooking?: Booking;
};

export type PendingConfirmation = ConfirmAndSaveArgs & { dryRunBooking: Booking };

export type ModalSubmit = {
  keptOldPrice: boolean;
  priceDifferenceCounterparty?: CounterpartyType;
  selectedCancellationFee: CancellationFee;
  overrideCancellationFeeComment?: string;
  storedPaymentMethodId?: string;
};

export const quoteTotal = (booking: Booking) => getContractTotal(booking.priceCalculation);

export const canKeepExistingLongTermPrice = (
  booking: Booking,
  updateBookingDto?: UpdateBookingDto,
): boolean => {
  if (!isLongTermPricing(booking.priceCalculation)) {
    return true;
  }
  if (updateBookingDto?.bookedCarId && updateBookingDto.bookedCarId !== booking.bookedCar.id) {
    return false;
  }
  const newStart = new Date(updateBookingDto?.startDate ?? booking.startDate);
  const newEnd = new Date(updateBookingDto?.endDate ?? booking.endDate);
  return isSameRentalLength(
    new Date(booking.startDate),
    new Date(booking.endDate),
    newStart,
    newEnd,
  );
};

export const resolveAllowKeepOldPrice = (
  booking: Booking,
  updateBookingDto: UpdateBookingDto | undefined,
  allowKeepOldPrice?: boolean,
): boolean => !!allowKeepOldPrice && canKeepExistingLongTermPrice(booking, updateBookingDto);

export const dryRunCancellationFee = (dryRun: Booking) =>
  asShortTermPricing(dryRun.priceCalculation)?.cancellationFee ?? CancellationFee.NO_FEE;

export const priceChanged = (booking: Booking, dryRun: Booking) =>
  hasBookingPriceChanged(quoteTotal(booking), quoteTotal(dryRun));

export const needsPriceChangeModal = (
  booking: Booking,
  dryRun: Booking,
  updateBookingDto?: UpdateBookingDto,
  confirmOnlyWhenCancellationFee?: boolean,
  priceDifferenceCounterparty?: CounterpartyType,
) => {
  if (updateBookingDto?.pricing?.bookedExtras === null) return true;
  const fee = dryRunCancellationFee(dryRun);
  if (fee && fee !== CancellationFee.NO_FEE) return true;
  if (showCounterpartySelect(booking, dryRun, priceDifferenceCounterparty)) return true;
  if (
    needsPostHandoverCharge(
      booking,
      dryRun,
      false,
      resolveCounterparty(booking, dryRun, priceDifferenceCounterparty),
    )
  ) {
    return true;
  }
  if (confirmOnlyWhenCancellationFee) return false;
  return priceChanged(booking, dryRun);
};

export const showCounterpartySelect = (
  booking: Booking,
  dryRun: Booking,
  fixedCounterparty?: CounterpartyType,
) =>
  requiresPriceDifferenceCounterparty(booking, priceChanged(booking, dryRun)) &&
  isAgencyPriceSplitBooking(booking) &&
  !fixedCounterparty;

export const resolveCounterparty = (
  booking: Booking,
  dryRun: Booking,
  fixedCounterparty?: CounterpartyType,
  selectedCounterparty?: CounterpartyType,
) => {
  if (!requiresPriceDifferenceCounterparty(booking, priceChanged(booking, dryRun)))
    return undefined;
  if (isCustomerPaidBooking(booking)) return CounterpartyType.CUSTOMER;
  if (isAgencyPriceSplitBooking(booking)) return fixedCounterparty ?? selectedCounterparty;
};

export const needsPostHandoverCharge = (
  booking: Booking,
  dryRun: Booking,
  keptOldPrice: boolean,
  counterparty?: CounterpartyType,
) => {
  if (keptOldPrice || booking.hasPeriodicInvoicing) return false;
  const diff = quoteTotal(dryRun) - quoteTotal(booking);
  if (diff <= 0 || !isAtLeastHandedOver(booking)) return false;
  if (isAgencyPriceSplitBooking(booking)) return counterparty === CounterpartyType.CUSTOMER;
  return (
    booking.invoicingType === InvoicingType.REGULAR ||
    booking.invoicingType === InvoicingType.COMMISSION ||
    booking.invoicingType === InvoicingType.COMPANY_REGULAR
  );
};

export const buildPersistPayload = (
  { booking, dryRunBooking, updateBookingDto, handoverAdjustment }: PendingConfirmation,
  submit: ModalSubmit,
  counterparty?: CounterpartyType,
  surchargeAmount?: number,
) => {
  const dryRunFee = dryRunCancellationFee(dryRunBooking);
  const overrideCancellationFee =
    submit.selectedCancellationFee !== dryRunFee ? submit.selectedCancellationFee : undefined;

  return {
    booking: {
      ...(updateBookingDto ?? {}),
      pricing: {
        ...(updateBookingDto?.pricing ?? {}),
        acceptedPrice: submit.keptOldPrice ? quoteTotal(booking) : quoteTotal(dryRunBooking),
        priceRecalculationMode: submit.keptOldPrice
          ? PriceRecalculationMode.USE_EXISTING
          : (updateBookingDto?.pricing?.priceRecalculationMode ??
            PriceRecalculationMode.RECALCULATE),
        ...(submit.keptOldPrice || !counterparty
          ? {}
          : { priceDifferenceCounterparty: counterparty }),
      },
      ...(submit.storedPaymentMethodId
        ? { storedPaymentMethodId: submit.storedPaymentMethodId, surchargeAmount }
        : {}),
    },
    query: {
      ...(handoverAdjustment ? { handoverAdjustment: true as const } : {}),
      ...(overrideCancellationFee !== undefined
        ? {
            overrideCancellationFee,
            overrideCancellationFeeComment: submit.overrideCancellationFeeComment,
          }
        : {}),
    },
  };
};

type PriceChangeConfirmationContext = {
  isBusy: Ref<boolean>;
  confirmAndSave: (args: ConfirmAndSaveArgs) => Promise<ConfirmAndSaveResult>;
  reset: () => void;
};

export const priceChangeConfirmationKey: InjectionKey<PriceChangeConfirmationContext> =
  Symbol('priceChangeConfirmation');

export const usePriceChangeConfirmation = () => {
  const context = inject(priceChangeConfirmationKey);
  if (!context) {
    throw new Error('usePriceChangeConfirmation requires PriceChangeConfirmationProvider');
  }
  return context;
};
